import _get from "lodash/get";
// eslint-disable-next-line import/no-cycle -- directive added automatically by Shepherd migration
import { getInstitution, getProduct } from './common'; // eslint-disable-next-line import/no-cycle -- directive added automatically by Shepherd migration

import { getNameWithFallbacks, getAdditionalData } from '../utils';
/**
 * Get the bottom line text.
 *
 * @param  {Object}  marketplaceEntity The `additional_data` augmented INV entity
 * @return {String|Object} The bottom line value.
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  return _get(getAdditionalData(marketplaceEntity), 'bottom_line');
};
/**
 * Get the "cons" bullets.
 *
 * @param       {Object}  marketplaceEntity The `additional_data` augmented INV entity
 * @return      {Array}   The "cons" array
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(getAdditionalData(marketplaceEntity), 'cons');
};
/**
 * Gets url to learn more page.
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {String}                   url
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(marketplaceEntity, 'product.details.nw_review_url.value');
};
/**
 * Gets the name of the entity as the alt text for the associated image
 *
 * @param     {Object} marketplaceEntity  The marketplace entity
 * @return    {string}                    Alt text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return getName(marketplaceEntity);
};
/**
 * Get the source URL of the image associated with the offer.
 *
 * @param      {Object}  marketplaceEntity  The investing entity
 * @return     {string}  The URL of the image.
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return _get(marketplaceEntity, 'product.institution.details.logo_url.value');
};
/**
 * Gets the issuer.
 *
 * @param {Object} marketplaceEntity The investing entity.
 *
 * @return {string} Issuer name.
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'name');
};
/**
 * Get the name to display for an offer.
 *
 * @param      {Object}  marketplaceEntity  The investing entity
 * @return     {string}  The offer name.
 */

export var getName = function getName(marketplaceEntity) {
  return getNameWithFallbacks(marketplaceEntity);
};
/**
 * Get the mininmum balance required to open the account.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {number}  The minimum balance required to open the account.
 */

export var getAccountOpenMin = function getAccountOpenMin(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.account_open_min.value');
};
/**
 * Get the advisor fee.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {string}  The advisor fee.
 */

export var getAdvisorFee = function getAdvisorFee(marketplaceEntity) {
  return _get(marketplaceEntity, 'product.details.advisor_fee.value');
};
/**
 * Get the advisor fee period.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {string}  The advisor fee period.
 */

export var getAdvisorFeePeriod = function getAdvisorFeePeriod(marketplaceEntity) {
  return _get(marketplaceEntity, 'product.details.advisor_fee_period.value');
};
/**
 * Get the flat trade price.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {number}  The flat trade price.
 */

export var getFlatTradePrice = function getFlatTradePrice(marketplaceEntity) {
  return _get(marketplaceEntity, 'product.details.flat_trade_price.value');
};
/**
 * Get the promotion description.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {string}  The promotion description.
 */

export var getPromotionDescription = function getPromotionDescription(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.promotion_description.value');
};
/**
 * Get the promotion value.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {string}  The promotion value.
 */

export var getPromotionValue = function getPromotionValue(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.promotion_value.value');
};
/**
 * Get the "pros" bullets.
 *
 * @param       {Object}  marketplaceEntity The `additional_data` augmented INV entity
 * @return      {Array}   The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(getAdditionalData(marketplaceEntity), 'pros');
};
/**
 * Get the slug.
 *
 * @todo This logic is a bit odd and should likely be updated to more
 *     intelligently pull from the canonical INV object, and not the
 *     associated WP review. Unfortunately this is currently required
 *     to maintain parity with review and roundup clients.
 *
 * @param {Object}  marketplaceEntity  The investing entity.
 * @return {string} The slug.
 */

export var getSlug = function getSlug(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.external_id') || _get(getAdditionalData(marketplaceEntity), 'slug') || _get(marketplaceEntity, 'details.wp_review_slug.value');
};
/**
 * Get the star rating.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {number}  The star rating.
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  return _get(getAdditionalData(marketplaceEntity), 'rating');
};
/**
 * Get the sub product type.
 *
 * @param     {Object} marketplaceEntity  The INV marketplace entity.
 * @return    {string}                    The sub product type.
 */

export var getSubProductType = function getSubProductType(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.type.value');
};
/**
 * Get the caption text for the trade price.
 *
 * @param      {Object}  marketplaceEntity  The INV entity.
 * @return     {string}  The trade price caption.
 */

export var getTradePriceCaption = function getTradePriceCaption(marketplaceEntity) {
  return _get(marketplaceEntity, 'product.details.trade_price_caption.value');
};